<template>
  <NavBar pageName="Settings"></NavBar>

  <v-container style="margin-top: 185px;">
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="d-flex align-center mb-4">
      <v-btn icon variant="text" size="small" @click="$router.push('/settings/imsi')" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h2>IMSI Inventory</h2>
    </div>

    <!-- Filters -->
    <v-row class="align-center mb-4">
      <v-col cols="12" md="3">
        <v-select
          v-model="filterReseller"
          :items="resellerOptions"
          item-title="text"
          item-value="value"
          label="Reseller"
          density="compact"
          clearable
          variant="outlined"
          hide-details
          @update:modelValue="loadData"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="searchTerm"
          label="Search IMSI / Serial"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          @keyup.enter="loadData"
        />
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end gap-2">
        <v-btn color="primary" variant="outlined" :loading="loading" @click="loadData">
          <v-icon class="mr-2">mdi-refresh</v-icon> Refresh
        </v-btn>
        <v-btn color="success" @click="openAddDialog">
          <v-icon class="mr-2">mdi-plus</v-icon> Add IMSI(s)
        </v-btn>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card>
      <v-card-text>
        <v-data-table-server
          :headers="tableHeaders"
          :items="items"
          :items-length="total"
          :loading="loading"
          :items-per-page="perPage"
          :page="page"
          @update:page="p => { page = p; loadData(); }"
          @update:items-per-page="pp => { perPage = pp; page = 1; loadData(); }"
          item-value="id"
          class="elevation-1"
        >
          <template #[`item.imsi`]="{ item }">
            <span class="font-weight-medium">{{ item.imsi }}</span>
          </template>
          <template #[`item.serial`]="{ item }">
            <span class="font-weight-medium">{{ item.serial }}</span>
          </template>
          <template #[`item.mno_status`]="{ item }">
            <v-chip size="small" variant="tonal">{{ item.mno_status }}</v-chip>
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ item.created_at ? new Date(item.created_at).toLocaleDateString() : '' }}
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn icon variant="text" size="small" @click="goToImsisList(item)" color="primary" title="Info">
              <v-icon size="small">mdi-eye</v-icon>
            </v-btn>
            <v-btn icon variant="text" size="small" @click="confirmDelete(item)" color="error">
              <v-icon size="small">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <!-- Add Dialog -->
    <v-dialog v-model="showAddDialog" max-width="600" persistent>
      <v-card>
        <v-card-title>Add IMSI(s)</v-card-title>
        <v-card-text>
          <v-btn-toggle v-model="addMode" mandatory color="primary" class="mb-4">
            <v-btn value="single">Single</v-btn>
            <v-btn value="range">Range</v-btn>
          </v-btn-toggle>

          <template v-if="addMode === 'single'">
            <v-text-field v-model="addForm.imsi" label="IMSI" type="number" variant="outlined" density="compact" class="mb-2" />
            <v-text-field v-model="addForm.serial" label="Serial" type="number" variant="outlined" density="compact" class="mb-2" />
          </template>
          <template v-else>
            <v-row>
              <v-col cols="6">
                <v-text-field v-model="addForm.imsi_from" label="IMSI From" type="number" variant="outlined" density="compact" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="addForm.imsi_to" label="IMSI To" type="number" variant="outlined" density="compact" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="addForm.serial_from" label="Serial From" type="number" variant="outlined" density="compact" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="addForm.serial_to" label="Serial To" type="number" variant="outlined" density="compact" />
              </v-col>
            </v-row>
            <v-alert v-if="rangeCount > 0" type="info" variant="tonal" density="compact" class="mb-2">
              Will create {{ rangeCount }} IMSI(s)
            </v-alert>
          </template>

          <v-select v-model="addForm.reseller_id" :items="resellerOptions.filter(r => r.value)" item-title="text" item-value="value" label="Reseller" variant="outlined" density="compact" class="mb-2" />
          <v-select v-model="addForm.mvno_id" :items="mvnoOptions" item-title="text" item-value="value" label="MVNO" variant="outlined" density="compact" class="mb-2" />
          <v-select v-model="addForm.imsi_type_id" :items="imsiTypeOptions" item-title="text" item-value="value" label="IMSI Type" variant="outlined" density="compact" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showAddDialog = false">Cancel</v-btn>
          <v-btn color="success" variant="flat" :loading="saving" @click="handleAdd">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Confirm Deactivate</v-card-title>
        <v-card-text>
          Mark IMSI <strong>{{ deleteTarget?.imsi }}</strong> (serial: {{ deleteTarget?.serial }}) as not available?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="handleDelete">Deactivate</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" location="bottom right">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import { getImsisInventory, addImsisInventory, deleteImsiInventory } from "@/composables/useInventoryAPI";
import { getImsiTypes } from "@/composables/useImsiTypesAPI";
import store from "@/store";

export default defineComponent({
  name: 'ImsiInventorySettings',
  components: { NavBar },
  data() {
    return {
      loading: false,
      saving: false,
      items: [],
      total: 0,
      page: 1,
      perPage: 50,
      filterReseller: null,
      searchTerm: '',
      showAddDialog: false,
      addMode: 'single',
      addForm: { imsi: '', serial: '', imsi_from: '', imsi_to: '', serial_from: '', serial_to: '', reseller_id: null, mvno_id: null, imsi_type_id: null },
      showDeleteDialog: false,
      deleteTarget: null,
      snackbar: { show: false, message: '', color: 'success' },
      resellerOptions: [
        { text: 'All', value: null },
        { text: '1 - Benefito', value: 1 },
        { text: '2 - NextM2M', value: 2 },
        { text: '3 - Valmed', value: 3 },
        { text: '4 - AdPos', value: 4 },
      ],
      mvnoOptions: [],
      imsiTypeOptions: [],
      tableHeaders: [
        { title: 'ID', key: 'id', width: '80px' },
        { title: 'IMSI', key: 'imsi' },
        { title: 'Serial', key: 'serial' },
        { title: 'Reseller', key: 'reseller_id', width: '100px' },
        { title: 'MVNO', key: 'mvno', width: '120px' },
        { title: 'IMSI Type', key: 'imsi_type', width: '120px' },
        { title: 'MNO Status', key: 'mno_status', width: '120px' },
        { title: 'Created', key: 'created_at', width: '110px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '80px' },
      ],
    };
  },
  computed: {
    rangeCount() {
      if (this.addMode !== 'range') return 0;
      const count = parseInt(this.addForm.imsi_to) - parseInt(this.addForm.imsi_from) + 1;
      return count > 0 ? count : 0;
    }
  },
  methods: {
    async loadData() {
      // Don't load without a search term or reseller filter
      if (!this.filterReseller && (!this.searchTerm || this.searchTerm.length < 3)) {
        this.items = [];
        this.total = 0;
        return;
      }
      this.loading = true;
      const token = store.getters.getToken;
      const params = { page: this.page, per_page: this.perPage };
      if (this.filterReseller) params.reseller_id = this.filterReseller;
      if (this.searchTerm && this.searchTerm.length >= 3) params.search = this.searchTerm;
      const res = await getImsisInventory(token, params);
      if (!res.error) {
        this.items = res.items || [];
        this.total = res.total || 0;
      } else {
        this.showSnackbar(res.message, 'error');
      }
      this.loading = false;
    },
    async loadLookups() {
      const token = store.getters.getToken;
      // Load IMSI types
      const typesRes = await getImsiTypes(token);
      if (!typesRes.error && typesRes.imsi_types) {
        this.imsiTypeOptions = typesRes.imsi_types.map(t => ({ text: t.name, value: t.id }));
      }
      // MVNOs hardcoded from DB values
      this.mvnoOptions = [
        { text: 'Veridian', value: 1 },
        { text: 'Benefito', value: 2 },
        { text: 'Vodafone Romania', value: 3 },
        { text: 'Orange Romania', value: 4 },
        { text: 'Digi Romania', value: 5 },
      ];
    },
    openAddDialog() {
      this.addForm = { imsi: '', serial: '', imsi_from: '', imsi_to: '', serial_from: '', serial_to: '', reseller_id: null, mvno_id: null, imsi_type_id: null };
      this.addMode = 'single';
      this.showAddDialog = true;
    },
    async handleAdd() {
      this.saving = true;
      const token = store.getters.getToken;
      const data = {
        mode: this.addMode,
        reseller_id: this.addForm.reseller_id,
        mvno_id: this.addForm.mvno_id,
        imsi_type_id: this.addForm.imsi_type_id,
      };
      if (this.addMode === 'single') {
        data.imsi = this.addForm.imsi;
        data.serial = this.addForm.serial;
      } else {
        data.imsi_from = this.addForm.imsi_from;
        data.imsi_to = this.addForm.imsi_to;
        data.serial_from = this.addForm.serial_from;
        data.serial_to = this.addForm.serial_to;
      }
      const res = await addImsisInventory(token, data);
      if (!res.error) {
        this.showSnackbar(res.message, 'success');
        this.showAddDialog = false;
        await this.loadData();
      } else {
        this.showSnackbar(res.message, 'error');
      }
      this.saving = false;
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.showDeleteDialog = true;
    },
    async handleDelete() {
      this.showDeleteDialog = false;
      this.loading = true;
      const token = store.getters.getToken;
      const res = await deleteImsiInventory(token, this.deleteTarget.id);
      if (!res.error) {
        this.showSnackbar(res.message, 'success');
        await this.loadData();
      } else {
        this.showSnackbar(res.message, 'error');
      }
      this.loading = false;
    },
    goToImsisList(item) {
      this.$router.push({ name: 'imsis', query: { search: item.imsi } });
    },
    showSnackbar(message, color) {
      this.snackbar = { show: true, message, color };
    }
  },
  mounted() {
    this.loadLookups();
    this.loadData();
  }
});
</script>
